import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { apiFetch } from '@/lib/api';

interface User {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  wechat: string;
  role: string;
  companyId: string;
  isSuperAdmin?: boolean;
}

interface Company {
  id: string;
  name: string;
  industries: string[];
}

interface AuthResponse {
  token: string;
  user: User;
  company: Company;
}

interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  user: User | null;
  company: Company | null;
  selectedEventId: string | null;
  signup: (companyName: string, fullName: string, email: string, password: string, phone: string, wechat?: string, ref?: string) => Promise<string | true>;
  acceptInvite: (token: string, fullName: string, email: string, password: string, phone?: string) => Promise<string | true>;
  loginWithPassword: (email: string, password: string) => Promise<string | true>;
  loginWithGoogle: (accessToken: string) => Promise<string | true>;
  loginWithSsoToken: (token: string) => void;
  setSelectedEventId: (eventId: string | null) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      isAuthenticated: false,
      token: null,
      user: null,
      company: null,
      selectedEventId: null,

      signup: async (companyName, fullName, email, password, phone, wechat, ref) => {
        try {
          const data = await apiFetch<AuthResponse>(
            '/api/auth/signup',
            {
              method: 'POST',
              body: JSON.stringify({ companyName, fullName, email, password, phone, ...(wechat ? { wechat } : {}), ...(ref ? { ref } : {}) }),
            },
          );
          set({
            isAuthenticated: true,
            token: data.token,
            user: data.user,
            company: data.company,
            selectedEventId: null,
          });
          return true;
        } catch (err) {
          if (err instanceof Error) return err.message;
          return 'Connection error. Please try again.';
        }
      },

      acceptInvite: async (token, fullName, email, password, phone) => {
        try {
          const data = await apiFetch<AuthResponse>(
            '/api/auth/accept-invite',
            {
              method: 'POST',
              body: JSON.stringify({ token, fullName, email, password, ...(phone ? { phone } : {}) }),
            },
          );
          set({
            isAuthenticated: true,
            token: data.token,
            user: data.user,
            company: data.company,
            selectedEventId: null,
          });
          return true;
        } catch (err) {
          if (err instanceof Error) return err.message;
          return 'Connection error. Please try again.';
        }
      },

      loginWithPassword: async (email, password) => {
        try {
          const data = await apiFetch<AuthResponse>(
            '/api/auth/login',
            { method: 'POST', body: JSON.stringify({ email, password }) },
          );
          set({
            isAuthenticated: true,
            token: data.token,
            user: data.user,
            company: data.company,
          });
          return true;
        } catch (err) {
          if (err instanceof Error) return err.message;
          return 'Invalid email or password.';
        }
      },

      loginWithGoogle: async (accessToken) => {
        try {
          const data = await apiFetch<AuthResponse>(
            '/api/auth/google',
            { method: 'POST', body: JSON.stringify({ accessToken }) },
          );
          set({
            isAuthenticated: true,
            token: data.token,
            user: data.user,
            company: data.company,
            selectedEventId: null,
          });
          return true;
        } catch (err) {
          if (err instanceof Error) return err.message;
          return 'Google sign-in failed. Please try again.';
        }
      },

      loginWithSsoToken: (ssoToken) => {
        // Decode the JWT payload (without verification — backend already verified)
        try {
          const payloadB64 = ssoToken.split('.')[1];
          const payload = JSON.parse(atob(payloadB64));
          set({
            isAuthenticated: true,
            token: ssoToken,
            user: {
              id: payload.sub,
              fullName: '',
              email: '',
              phone: '',
              wechat: '',
              role: 'admin',
              companyId: payload.companyId,
              isSuperAdmin: payload.isSuperAdmin || false,
            },
            company: null,
            selectedEventId: null,
          });
        } catch {
          console.error('[Auth] Failed to decode SSO token');
        }
      },

      setSelectedEventId: (eventId) => {
        set({ selectedEventId: eventId });
      },

      logout: () => {
        set({
          isAuthenticated: false,
          token: null,
          user: null,
          company: null,
          selectedEventId: null,
        });
      },
    }),
    {
      name: 'reevol-auth',
      version: 4, // bumped from 3 → 4 for SSO support
      storage: createJSONStorage(() => localStorage),
      migrate: () => {
        // Any pre-v4 state — force re-login
        return {
          isAuthenticated: false,
          token: null,
          user: null,
          company: null,
          selectedEventId: null,
        };
      },
    },
  ),
);
